import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import FitScreenIcon from '@mui/icons-material/FitScreen';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { toErrorMessage } from './preOrderApi';
import { blobErrorMessage, fetchHeicPreview, fetchSourceTable, type TablePreview } from './preOrderDocumentsApi';
import { readTiff, renderTiffPage, type TiffDocument } from './tiffPages';

/**
 * Podgląd dokumentu przy prezleceniu (03.11.2026, `preOrderDocuments`,
 * S-03.11-01): obraz z powiększeniem i obrotem, skan TIFF strona po
 * stronie, PDF w przeglądarce PDF wbudowanej w przeglądarkę (strony,
 * powiększenie i obrót w jej pasku — bez zewnętrznych bibliotek i CDN)
 * oraz arkusz jako tabela przygotowana na serwerze.
 *
 * Bezpieczeństwo: plik przychodzi z naszej trasy jako `blob:`; obraz (także
 * SVG) tylko w `<img>` — przeglądarka nie wykonuje wtedy skryptów z pliku;
 * nic z pliku nie trafia do drzewa strony.
 */

export const ZOOM_STEPS = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2, 3, 4] as const;

/** Następny krok powiększenia w stronę `dir` (+1 / −1). */
export function nextZoom(current: number, dir: 1 | -1): number {
  if (dir > 0) return ZOOM_STEPS.find((step) => current + 1e-9 < step) ?? ZOOM_STEPS[ZOOM_STEPS.length - 1];
  return [...ZOOM_STEPS].reverse().find((step) => current - 1e-9 > step) ?? ZOOM_STEPS[0];
}

/** Obrót o 90° w prawo (+1) albo w lewo (−1), w zakresie 0–270. */
export function nextRotation(current: number, dir: 1 | -1): number {
  return (((current + dir * 90) % 360) + 360) % 360;
}

/** Obraz z powiększeniem i obrotem (zdjęcie, skan, strona TIFF, SVG). */
export const ImageViewer = ({ src, alt, testId = 'source-preview-image' }: { src: string; alt: string; testId?: string }) => {
  // `null` = dopasuj do szerokości ramki.
  const [zoom, setZoom] = useState<number | null>(null);
  const [rotation, setRotation] = useState(0);
  useEffect(() => {
    setZoom(null);
    setRotation(0);
  }, [src]);
  const sideways = rotation === 90 || rotation === 270;
  return (
    <Box>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5, flexWrap: 'wrap' }} useFlexGap data-testid="document-viewer-toolbar">
        <Tooltip title="Pomniejsz">
          <IconButton size="small" aria-label="Pomniejsz" onClick={() => setZoom((level) => nextZoom(level ?? 1, -1))} data-testid="document-zoom-out">
            <ZoomOutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="caption" sx={{ minWidth: 72, textAlign: 'center' }} data-testid="document-zoom-level">
          {zoom === null ? 'dopasowane' : `${Math.round(zoom * 100)}%`}
        </Typography>
        <Tooltip title="Powiększ">
          <IconButton size="small" aria-label="Powiększ" onClick={() => setZoom((level) => nextZoom(level ?? 1, 1))} data-testid="document-zoom-in">
            <ZoomInIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Dopasuj do szerokości">
          <IconButton size="small" aria-label="Dopasuj do szerokości" onClick={() => setZoom(null)} data-testid="document-zoom-fit">
            <FitScreenIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Obróć w lewo">
          <IconButton size="small" aria-label="Obróć w lewo" onClick={() => setRotation((r) => nextRotation(r, -1))} data-testid="document-rotate-left">
            <RotateLeftIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Obróć w prawo">
          <IconButton size="small" aria-label="Obróć w prawo" onClick={() => setRotation((r) => nextRotation(r, 1))} data-testid="document-rotate-right">
            <RotateRightIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{ overflow: 'auto', maxHeight: 640, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover', textAlign: 'center' }}
        data-testid="document-viewer-frame"
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          data-testid={testId}
          data-rotation={rotation}
          data-zoom={zoom ?? 'fit'}
          sx={{
            display: 'inline-block',
            transform: `rotate(${rotation}deg)`,
            transformOrigin: 'center center',
            // Dopasowane: obraz na szerokość ramki (przy obrocie — na wysokość).
            ...(zoom === null
              ? { maxWidth: sideways ? 600 : '100%', maxHeight: sideways ? '100%' : 'none' }
              : { width: `${zoom * 100}%`, maxWidth: 'none' }),
            my: sideways ? 8 : 0,
          }}
        />
      </Box>
    </Box>
  );
};

/** „Strona 2 z 3”. */
export function pageLabel(page: number, count: number): string {
  return ['Strona', String(page + 1), 'z', String(count)].join(' ');
}

/** Skan TIFF — strony dekodowane w przeglądarce, przełączane strzałkami. */
export const TiffViewer = ({ blob, filename }: { blob: Blob; filename: string }) => {
  const [doc, setDoc] = useState<TiffDocument | null>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(0);
  useEffect(() => {
    let alive = true;
    setDoc(null);
    setFailed(false);
    setPage(0);
    blob
      .arrayBuffer()
      .then((buf) => {
        if (!alive) return;
        const d = readTiff(buf);
        if (d.pages.length === 0) setFailed(true);
        else setDoc(d);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [blob]);
  const image = useMemo(() => {
    if (!doc) return null;
    try {
      return renderTiffPage(doc, page);
    } catch {
      return null;
    }
  }, [doc, page]);
  if (failed) {
    return (
      <Alert severity="warning" data-testid="document-tiff-error">
        Tego skanu nie udało się pokazać w aplikacji — pobierz oryginał i otwórz go u siebie.
      </Alert>
    );
  }
  if (!doc) return <CircularProgress size={20} />;
  const count = doc.pages.length;
  return (
    <Box data-testid="document-tiff" data-pages={count}>
      {count > 1 ? (
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', mb: 0.5 }}>
          <IconButton size="small" aria-label="Poprzednia strona" disabled={page === 0} onClick={() => setPage((p) => p - 1)} data-testid="document-page-prev">
            <NavigateBeforeIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" data-testid="document-page-label">
            {pageLabel(page, count)}
          </Typography>
          <IconButton size="small" aria-label="Następna strona" disabled={page >= count - 1} onClick={() => setPage((p) => p + 1)} data-testid="document-page-next">
            <NavigateNextIcon fontSize="small" />
          </IconButton>
        </Stack>
      ) : null}
      {image ? (
        <ImageViewer src={image} alt={`${filename} — strona ${page + 1}`} />
      ) : (
        <Alert severity="warning" data-testid="document-tiff-page-too-large">
          Strona {page + 1} jest za duża na podgląd w aplikacji — pobierz oryginał.
        </Alert>
      )}
    </Box>
  );
};

/**
 * Zdjęcie HEIC (03.11.2026, decyzja Bartka): serwer przelicza pierwszy obraz
 * na JPEG albo PNG, tu pokazujemy go jak każdy obraz. Błąd — zdanie z serwera
 * i „Pobierz oryginał” pod spodem (przycisk stoi przy każdym pliku).
 */
export const HeicViewer = ({
  preOrderId,
  origin,
  fileId,
  filename,
}: {
  preOrderId: number;
  origin: { emailId: number } | 'client';
  fileId: number;
  filename: string;
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [count, setCount] = useState(1);
  const [error, setError] = useState<string | null>(null);
  const originKey = origin === 'client' ? 'client' : origin.emailId;
  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);
    fetchHeicPreview(preOrderId, origin, fileId)
      .then(({ blob, imageCount }) => {
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setCount(imageCount);
        setUrl(objectUrl);
      })
      .catch(async (e) => {
        const msg = (await blobErrorMessage(e)) ?? 'Tego zdjęcia nie udało się pokazać w aplikacji — pobierz plik.';
        if (alive) setError(msg);
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // `origin` jako obiekt zmienia się przy każdym rysowaniu — klucz wystarcza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preOrderId, originKey, fileId]);
  if (error) {
    return (
      <Alert severity="info" data-testid="source-preview-no-preview">
        {error}
      </Alert>
    );
  }
  if (!url) return <CircularProgress size={20} />;
  return (
    <Box data-testid="document-heic" data-images={count}>
      {count > 1 ? (
        <Alert severity="info" sx={{ mb: 1 }} data-testid="document-heic-more">
          {heicCountNote(count)}
        </Alert>
      ) : null}
      <ImageViewer src={url} alt={filename} />
    </Box>
  );
};

/** „W pliku jest 2 zdjęcia…” — ile obrazów ma HEIC (seria, zdjęcie „Live”). */
export function heicCountNote(count: number): string {
  const few = count % 10 >= 2 && count % 10 <= 4 && (count % 100 < 12 || count % 100 > 14);
  const amount = few ? `są ${count} zdjęcia` : `jest ${count} zdjęć`;
  return `W pliku ${amount} (np. seria) — pokazujemy pierwsze; wszystkie są w oryginale do pobrania.`;
}

/** PDF w przeglądarce PDF wbudowanej w przeglądarkę — strony, powiększenie, obrót w jej pasku. */
export const PdfViewer = ({ url, filename }: { url: string; filename: string }) => (
  <Box
    component="iframe"
    src={`${url}#view=FitH`}
    title={filename}
    data-testid="source-preview-pdf"
    sx={{ width: '100%', height: 680, border: '1px solid', borderColor: 'divider' }}
  />
);

/** Arkusz jako tabela (wartości z serwera, bez formuł i makr). */
export const TableViewer = ({
  preOrderId,
  origin,
  fileId,
}: {
  preOrderId: number;
  origin: { emailId: number } | 'client';
  fileId: number;
}) => {
  const [table, setTable] = useState<TablePreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sheet, setSheet] = useState(0);
  const originKey = origin === 'client' ? 'client' : origin.emailId;
  useEffect(() => {
    let alive = true;
    setTable(null);
    setError(null);
    setSheet(0);
    fetchSourceTable(preOrderId, origin, fileId)
      .then((t) => alive && setTable(t))
      .catch((e) => alive && setError(toErrorMessage(e)));
    return () => {
      alive = false;
    };
    // `origin` jako obiekt zmienia się przy każdym rysowaniu — klucz wystarcza.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preOrderId, originKey, fileId]);
  if (error) {
    return (
      <Alert severity="info" data-testid="source-preview-no-preview">
        {error}
      </Alert>
    );
  }
  if (!table) return <CircularProgress size={20} />;
  if (table.sheets.length === 0) {
    return (
      <Alert severity="info" data-testid="source-preview-no-preview">
        Arkusz jest pusty.
      </Alert>
    );
  }
  const current = table.sheets[Math.min(sheet, table.sheets.length - 1)];
  const width = Math.max(1, ...current.rows.map((r) => r.length));
  const colName = (i: number) => {
    let s = '';
    for (let n = i + 1; n > 0; n = Math.floor((n - 1) / 26)) s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
    return s;
  };
  return (
    <Box data-testid="document-table">
      {table.sheets.length > 1 ? (
        <Stack direction="row" spacing={0.5} useFlexGap sx={{ flexWrap: 'wrap', mb: 1 }} role="tablist" aria-label="Arkusze">
          {table.sheets.map((s, i) => (
            <Chip
              key={`${s.name}-${i}`}
              size="small"
              role="tab"
              aria-selected={i === sheet}
              label={s.name}
              color={i === sheet ? 'primary' : 'default'}
              variant={i === sheet ? 'filled' : 'outlined'}
              onClick={() => setSheet(i)}
              data-testid="document-table-sheet"
            />
          ))}
        </Stack>
      ) : null}
      <Box sx={{ overflow: 'auto', maxHeight: 600, border: '1px solid', borderColor: 'divider' }}>
        <Table size="small" stickyHeader data-testid="document-table-grid">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 36, color: 'text.secondary' }} />
              {Array.from({ length: width }, (_, i) => (
                <TableCell key={i} sx={{ color: 'text.secondary', fontWeight: 600 }}>
                  {colName(i)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {current.rows.map((row, r) => (
              <TableRow key={r}>
                <TableCell sx={{ color: 'text.secondary' }}>{r + 1}</TableCell>
                {Array.from({ length: width }, (_, c) => (
                  <TableCell key={c} sx={{ whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }} title={row[c] ?? ''}>
                    {row[c] ?? ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
        {table.note}
        {current.truncated ? ' Arkusz jest dłuższy albo szerszy — pokazujemy początek.' : ''}
        {table.moreSheets ? ' Skoroszyt ma więcej arkuszy niż pokazujemy.' : ''}
      </Typography>
    </Box>
  );
};
